import { X, Download, FileText, Table } from "lucide-react";
import { InventoryItem } from "../types";
import { IconButton } from "./ui/button";

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  items: InventoryItem[];
  selectedIds: string[];
}

export function ExportDialog({
  isOpen,
  onClose,
  items,
  selectedIds,
}: ExportDialogProps) {
  if (!isOpen) return null;

  const exportToCSV = (itemsToExport: InventoryItem[]) => {
    const headers = [
      "ID",
      "Nom",
      "Catégorie",
      "Prix Vente",
      "Prix Coûtant",
      "Marge",
      "Marge %",
      "Quantité",
      "Statut",
    ];

    const rows = itemsToExport.map((item) => {
      const marge = item.prixVente - item.prixCoutant;
      const margePercent =
        item.prixCoutant > 0
          ? ((item.prixVente - item.prixCoutant) / item.prixCoutant) * 100
          : 0;

      return [
        item.id,
        item.nom,
        item.categorie,
        item.prixVente.toFixed(2),
        item.prixCoutant.toFixed(2),
        marge.toFixed(2),
        margePercent.toFixed(2),
        item.quantite,
        item.statut,
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `inventaire_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    onClose();
  };

  const handleExportAll = () => {
    exportToCSV(items);
  };

  const handleExportSelected = () => {
    const selectedItems = items.filter((item) =>
      selectedIds.includes(item.id)
    );
    exportToCSV(selectedItems);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[var(--card)] rounded-xl shadow-xl border border-[var(--border)] w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--color-steel-bg)] rounded-lg">
              <Download className="w-5 h-5 text-[var(--color-steel)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--foreground)]">
              Exporter les données
            </h2>
          </div>
          <IconButton
            icon={X}
            variant="ghost"
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-[var(--muted-foreground)]">
            Choisissez le type d'exportation
          </p>

          {/* Export All */}
          <button
            onClick={handleExportAll}
            className="w-full p-4 bg-[var(--muted)] hover:bg-[var(--switch-background)] border border-[var(--switch-background)] rounded-lg transition-colors text-left group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[var(--color-steel-bg)] rounded-lg group-hover:bg-[var(--color-steel-dark)]/30 transition-colors">
                <Table className="w-5 h-5 text-[var(--color-steel)]" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-[var(--foreground)]">
                  Tous les articles
                </div>
                <div className="text-sm text-[var(--muted-foreground)]">
                  {items.length} article(s)
                </div>
              </div>
            </div>
          </button>

          {/* Export Selected */}
          {selectedIds.length > 0 && (
            <button
              onClick={handleExportSelected}
              className="w-full p-4 bg-[var(--muted)] hover:bg-[var(--switch-background)] border border-[var(--switch-background)] rounded-lg transition-colors text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[var(--color-olive-bg)] rounded-lg group-hover:bg-[var(--color-olive)]/30 transition-colors">
                  <FileText className="w-5 h-5 text-[var(--color-olive)]" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-[var(--foreground)]">
                    Articles sélectionnés
                  </div>
                  <div className="text-sm text-[var(--muted-foreground)]">
                    {selectedIds.length} article(s)
                  </div>
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--border)]">
          <p className="text-xs text-[var(--muted-foreground-light)] text-center">
            Les données seront exportées au format CSV
          </p>
        </div>
      </div>
    </div>
  );
}